import { createHttpClient } from '@adapters/HttpClient';
import { Plus } from 'lucide-react';
import { Button } from '@components/ui/button/Button';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { apiClientWH } from '@shared/services';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RolesApiAdapter } from '../../adapters/RolesApiAdapter';
import { CreateModal, CreateModalRef } from '../modals/CreateModal';

interface CreateRoleButtonProps {
  onRoleCreated?: () => void;
}

/**
 * Componente para el botón de crear rol
 * Maneja el modal de creación y permisos de creación
 */
export const CreateRoleButton: React.FC<CreateRoleButtonProps> = ({ onRoleCreated }) => {
  const { t } = useTranslation('rolesModule');
  const { toast } = useToast();
  const { isAdminRole } = usePermissions();
  const createModalRef = useRef<CreateModalRef>(null);

  const handleCreateRole = () => {
    if (createModalRef.current && typeof createModalRef.current.open === 'function') {
      createModalRef.current.open({
        mode: 'create',
        onSubmit: async (name, permissions) => {
          const httpClient = createHttpClient(apiClientWH);
          const apiAdapter = new RolesApiAdapter(httpClient);
          try {
            await apiAdapter.createRole(name, permissions);
            toast({
              title: t('toast.createSuccess', 'Rol creado correctamente.'),
            });
            onRoleCreated?.();
          } catch (e: any) {
            const apiMsg =
              e?.response?.data?.message || e?.message || t('toast.createError', 'No se pudo crear el rol.');
            toast({
              title: t('toast.createError', 'No se pudo crear el rol.'),
              description: apiMsg,
              variant: 'destructive',
            });
          }
        },
      });
    }
  };

  // Verificar si el usuario es admin (OBLIGATORIO)
  if (!isAdminRole()) {
    return null;
  }

  return (
    <>
      <Button variant="primary" size="default" data-testid="create-role-button" onClick={handleCreateRole}>
        <Plus className="h-4 w-4" aria-hidden />
        {t('buttons.create')}
      </Button>
      <CreateModal ref={createModalRef} />
    </>
  );
};
